import { PageHero } from '@/components/PageHero';
import { DepthLadder } from '@/components/fx/Schematics';
import { TerminalPanel } from '@/components/fx/TerminalPanel';
import { Reveal } from '@/components/fx/Reveal';
import { Headline } from '@/components/fx/Headline';
import { Ring } from '@/components/fx/Ring';
import { TiltCard } from '@/components/fx/TiltCard';
import type { PlatformVals } from '@/lib/vals/platform';

export function PlatformPage({ v }: { v: PlatformVals }) {
  return (
    <main id="wft-main" tabIndex={-1}>
      <PageHero
        eyebrow="Platform"
        title="Every section, every panel, and what feeds it."
        highlight={['feeds', 'it.']}
        lede="Sections and panels are declared once, in one registry. The rail, the command bar's autocomplete, the keyboard leader map and every route read that same declaration, so a section cannot exist in the navigation and not in the router."
        aside={
          <TerminalPanel mnemonic="CRY" title="Order book depth">
            <DepthLadder />
          </TerminalPanel>
        }
      />

      {/* Coverage per cluster */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            What is bound, cluster by cluster
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="Coverage is counted, not claimed."
            highlight={['counted,']}
          />

          <div className="coverage">
            {v.coverage.map((c, i) => (
              <Reveal key={c.label} delay={i * 90}>
                <TiltCard className="cov glass" max={3}>
                  <div className="cov__head">
                    <h3>{c.label}</h3>
                    <Ring
                      value={Number(c.bound)}
                      total={Number(c.total)}
                      label={`${c.label}: ${c.bound} of ${c.total} panels bound`}
                    />
                  </div>
                  <p className="cov__meta">{c.meta}</p>
                  <div className="cov__cells" aria-hidden="true">
                    {c.cells.map((cell, j) => (
                      <i key={j} title={cell.title} style={{ background: cell.color }} />
                    ))}
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Panel anatomy */}
      <section className="section">
        <div className="shell split">
          <div className="split__lead">
            <Reveal as="p" className="eyebrow">
              Panel anatomy
            </Reveal>
            <Headline
              as="h2"
              className="display display--section"
              text="The panel decides, not the widget."
              highlight={['decides,']}
            />
            <Reveal as="p" className="lede" delay={200}>
              Each panel owns its own header, banner, body and bindings. A widget never decides
              whether it has data — the panel does, and it picks the honest render.
            </Reveal>
          </div>

          <div className="guards">
            {v.anatomy.map((a, i) => (
              <Reveal key={a.part} delay={i * 80}>
                <div className="guard glass">
                  <span className="guard__index">{a.part}</span>
                  <p className="guard__rule">{a.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            Principles
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="Four rules the frame never breaks."
            highlight={['never', 'breaks.']}
          />
          <div className="lanes">
            {v.principles.map((p, i) => (
              <Reveal key={p.title} delay={i * 100}>
                <TiltCard className="lane glass" max={4}>
                  <span className="lane__bar" style={{ background: 'var(--engine)' }} />
                  <h3 className="lane__name">{p.title}</h3>
                  <p className="lane__body">{p.body}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Registry */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            The registry, in full
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="Twenty sections, declared once."
            highlight={['once.']}
          />

          <div className="registry">
            {v.sectionList.map((s, i) => (
              <Reveal key={s.mnemonic} delay={Math.min(i, 8) * 45}>
                <details className="reg glass">
                  <summary>
                    <code className="reg__mn">{s.mnemonic}</code>
                    <span className="reg__title">{s.title}</span>
                    <span className="reg__purpose">{s.purpose}</span>
                    <span className="reg__meta">{s.meta}</span>
                  </summary>
                  <ul className="reg__panels">
                    {s.panels.map((p) => (
                      <li key={p.title}>
                        <span className="reg__panelTitle">{p.title}</span>
                        <code className="reg__kind">{p.kind}</code>
                        <span className="reg__feed" style={{ color: p.feedColor }}>
                          {p.feed}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
